import React, { useState, useEffect } from 'react'
import { Calendar, Settings, Bell, Clock, Globe, Users, Save, RefreshCw, Palette, Eye } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { useDemoMode } from '@/components/DemoModeBanner'

interface CalendarConfig {
  default_view: 'month' | 'week' | 'day' | 'list'
  week_starts_on: 0 | 1 | 6 // 0=Sunday, 1=Monday, 6=Saturday
  show_weekends: boolean
  show_week_numbers: boolean
  time_format: '12h' | '24h'
  date_format: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD'
  timezone: string
  default_event_duration: number // in minutes
  working_hours_start: string
  working_hours_end: string

  // Event types visibility
  show_chores: boolean
  show_appointments: boolean
  show_school_events: boolean
  show_holidays: boolean
  show_custody_schedule: boolean
  show_family_events: boolean

  // Colors
  chore_color: string
  appointment_color: string
  school_event_color: string
  holiday_color: string
  custody_color: string
  family_event_color: string

  // Notifications
  enable_reminders: boolean
  default_reminder_minutes: number
  enable_email_notifications: boolean
  enable_push_notifications: boolean

  // Sharing
  calendar_public: boolean
  share_url?: string
  ical_feed_enabled: boolean
  google_calendar_sync: boolean
  apple_calendar_sync: boolean
}

export const CalendarSettings: React.FC = () => {
  const [config, setConfig] = useState<CalendarConfig>({
    default_view: 'month',
    week_starts_on: 0,
    show_weekends: true,
    show_week_numbers: false,
    time_format: '12h',
    date_format: 'MM/DD/YYYY',
    timezone: 'America/New_York',
    default_event_duration: 60,
    working_hours_start: '08:00',
    working_hours_end: '17:00',

    show_chores: true,
    show_appointments: true,
    show_school_events: true,
    show_holidays: true,
    show_custody_schedule: true,
    show_family_events: true,

    chore_color: '#3B82F6',
    appointment_color: '#10B981',
    school_event_color: '#F59E0B',
    holiday_color: '#EF4444',
    custody_color: '#8B5CF6',
    family_event_color: '#EC4899',

    enable_reminders: true,
    default_reminder_minutes: 15,
    enable_email_notifications: false,
    enable_push_notifications: false,

    calendar_public: false,
    ical_feed_enabled: false,
    google_calendar_sync: false,
    apple_calendar_sync: false
  })

  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'display' | 'events' | 'colors' | 'notifications' | 'sharing'>('display')
  const { isDemoMode } = useDemoMode()

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get('/api/admin/settings/calendar')
      setConfig(data)
    } catch (error) {
      console.error('Failed to load calendar settings:', error)
      // Use defaults if no settings exist
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      await apiClient.put('/api/admin/settings/calendar', config)
      toast.success('Calendar settings saved successfully')
    } catch (error) {
      toast.error('Failed to save calendar settings')
    }
  }

  const renderDisplayTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Default View</label>
          <select
            value={config.default_view}
            onChange={(e) => setConfig({ ...config, default_view: e.target.value as any })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="month">Month</option>
            <option value="week">Week</option>
            <option value="day">Day</option>
            <option value="list">List</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Week Starts On</label>
          <select
            value={config.week_starts_on}
            onChange={(e) => setConfig({ ...config, week_starts_on: parseInt(e.target.value) as any })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="0">Sunday</option>
            <option value="1">Monday</option>
            <option value="6">Saturday</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Time Format</label>
          <select
            value={config.time_format}
            onChange={(e) => setConfig({ ...config, time_format: e.target.value as any })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="12h">12-hour (AM/PM)</option>
            <option value="24h">24-hour</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Date Format</label>
          <select
            value={config.date_format}
            onChange={(e) => setConfig({ ...config, date_format: e.target.value as any })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Working Hours Start</label>
          <input
            type="time"
            value={config.working_hours_start}
            onChange={(e) => setConfig({ ...config, working_hours_start: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Working Hours End</label>
          <input
            type="time"
            value={config.working_hours_end}
            onChange={(e) => setConfig({ ...config, working_hours_end: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.show_weekends}
            onChange={(e) => setConfig({ ...config, show_weekends: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Show weekends</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.show_week_numbers}
            onChange={(e) => setConfig({ ...config, show_week_numbers: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Show week numbers</span>
        </label>
      </div>
    </div>
  )

  const renderEventsTab = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-700">Visible Event Types</h4>
      <div className="space-y-2">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.show_chores}
            onChange={(e) => setConfig({ ...config, show_chores: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Chores & Assignments</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.show_appointments}
            onChange={(e) => setConfig({ ...config, show_appointments: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Appointments</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.show_school_events}
            onChange={(e) => setConfig({ ...config, show_school_events: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">School Events</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.show_holidays}
            onChange={(e) => setConfig({ ...config, show_holidays: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Holidays</span>
        </label>

        {!isDemoMode && (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.show_custody_schedule}
              onChange={(e) => setConfig({ ...config, show_custody_schedule: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Custody Schedule</span>
          </label>
        )}

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.show_family_events}
            onChange={(e) => setConfig({ ...config, show_family_events: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Family Events</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Default Event Duration (minutes)</label>
        <input
          type="number"
          value={config.default_event_duration}
          onChange={(e) => setConfig({ ...config, default_event_duration: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border rounded-lg"
          min="15"
          step="15"
        />
      </div>
    </div>
  )

  const renderColorsTab = () => (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-700">Event Colors</h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Chores</label>
          <div className="flex items-center">
            <input
              type="color"
              value={config.chore_color}
              onChange={(e) => setConfig({ ...config, chore_color: e.target.value })}
              className="mr-2"
            />
            <input
              type="text"
              value={config.chore_color}
              onChange={(e) => setConfig({ ...config, chore_color: e.target.value })}
              className="flex-1 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Appointments</label>
          <div className="flex items-center">
            <input
              type="color"
              value={config.appointment_color}
              onChange={(e) => setConfig({ ...config, appointment_color: e.target.value })}
              className="mr-2"
            />
            <input
              type="text"
              value={config.appointment_color}
              onChange={(e) => setConfig({ ...config, appointment_color: e.target.value })}
              className="flex-1 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">School Events</label>
          <div className="flex items-center">
            <input
              type="color"
              value={config.school_event_color}
              onChange={(e) => setConfig({ ...config, school_event_color: e.target.value })}
              className="mr-2"
            />
            <input
              type="text"
              value={config.school_event_color}
              onChange={(e) => setConfig({ ...config, school_event_color: e.target.value })}
              className="flex-1 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Holidays</label>
          <div className="flex items-center">
            <input
              type="color"
              value={config.holiday_color}
              onChange={(e) => setConfig({ ...config, holiday_color: e.target.value })}
              className="mr-2"
            />
            <input
              type="text"
              value={config.holiday_color}
              onChange={(e) => setConfig({ ...config, holiday_color: e.target.value })}
              className="flex-1 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Custody</label>
          <div className="flex items-center">
            <input
              type="color"
              value={config.custody_color}
              onChange={(e) => setConfig({ ...config, custody_color: e.target.value })}
              className="mr-2"
            />
            <input
              type="text"
              value={config.custody_color}
              onChange={(e) => setConfig({ ...config, custody_color: e.target.value })}
              className="flex-1 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Family Events</label>
          <div className="flex items-center">
            <input
              type="color"
              value={config.family_event_color}
              onChange={(e) => setConfig({ ...config, family_event_color: e.target.value })}
              className="mr-2"
            />
            <input
              type="text"
              value={config.family_event_color}
              onChange={(e) => setConfig({ ...config, family_event_color: e.target.value })}
              className="flex-1 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>
      </div>
    </div>
  )

  const renderNotificationsTab = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.enable_reminders}
            onChange={(e) => setConfig({ ...config, enable_reminders: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable event reminders</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.enable_email_notifications}
            onChange={(e) => setConfig({ ...config, enable_email_notifications: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable email notifications</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.enable_push_notifications}
            onChange={(e) => setConfig({ ...config, enable_push_notifications: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable push notifications</span>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Default Reminder (minutes before)</label>
        <select
          value={config.default_reminder_minutes}
          onChange={(e) => setConfig({ ...config, default_reminder_minutes: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border rounded-lg"
          disabled={!config.enable_reminders}
        >
          <option value="5">5 minutes</option>
          <option value="10">10 minutes</option>
          <option value="15">15 minutes</option>
          <option value="30">30 minutes</option>
          <option value="60">1 hour</option>
          <option value="120">2 hours</option>
          <option value="1440">1 day</option>
        </select>
      </div>
    </div>
  )

  const renderSharingTab = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.calendar_public}
            onChange={(e) => setConfig({ ...config, calendar_public: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Make calendar public</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.ical_feed_enabled}
            onChange={(e) => setConfig({ ...config, ical_feed_enabled: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Enable iCal feed</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.google_calendar_sync}
            onChange={(e) => setConfig({ ...config, google_calendar_sync: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Sync with Google Calendar</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.apple_calendar_sync}
            onChange={(e) => setConfig({ ...config, apple_calendar_sync: e.target.checked })}
            className="mr-2"
          />
          <span className="text-sm">Sync with Apple Calendar</span>
        </label>
      </div>

      {config.ical_feed_enabled && (
        <div>
          <label className="block text-sm font-medium mb-2">iCal Feed URL</label>
          {/* Feeds are per-family and token-based (/api/calendar/:token), so
              there is no fixed URL to show here. */}
          <p className="text-sm text-gray-600">
            Generate your family's private feed link under Calendar Subscription below.
          </p>
        </div>
      )}
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Calendar Settings</h2>
        <p className="text-gray-600">Configure calendar display and behavior preferences</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('display')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'display'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Display
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'events'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Events
          </button>
          <button
            onClick={() => setActiveTab('colors')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'colors'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Palette className="w-4 h-4 inline mr-2" />
            Colors
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'notifications'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('sharing')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'sharing'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Globe className="w-4 h-4 inline mr-2" />
            Sharing
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'display' && renderDisplayTab()}
          {activeTab === 'events' && renderEventsTab()}
          {activeTab === 'colors' && renderColorsTab()}
          {activeTab === 'notifications' && renderNotificationsTab()}
          {activeTab === 'sharing' && renderSharingTab()}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={loadSettings}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Reset
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Save className="w-4 h-4 inline mr-2" />
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}